import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  EmailAlreadyRegisteredError,
  InvalidCompanyCodeError,
  NetworkError,
  signUp,
  startGoogleOAuth,
  validateCompanyCode,
} from '../services/authService';

type FormState = {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  companyCode: string;
  employeeId: string;
};

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyCode: '',
    employeeId: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const canSubmit = useMemo(() => {
    return (
      form.fullName.trim().length > 0 &&
      form.email.trim().length > 0 &&
      form.password.length > 0 &&
      form.confirmPassword.length > 0 &&
      form.companyCode.trim().length > 0 &&
      form.employeeId.trim().length > 0
    );
  }, [form]);

  const mapError = (error: unknown): string => {
    if (
      error instanceof InvalidCompanyCodeError ||
      error instanceof EmailAlreadyRegisteredError ||
      error instanceof NetworkError
    ) {
      return error.message;
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'Something went wrong. Try again.';
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
    if (errorMessage) {
      setErrorMessage(null);
    }
    if (successMessage) {
      setSuccessMessage(null);
    }
  };

  const validateForm = (): string | null => {
    if (!form.fullName.trim()) {
      return 'Full name is required';
    }
    if (form.fullName.trim().length < 2) {
      return 'Name must be at least 2 characters';
    }

    if (!form.email.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      return 'Enter a valid work email';
    }

    if (!form.password) {
      return 'Password is required';
    }
    const passwordRegex = /^(?=.*[A-Z])(?=.*[0-9]).{8,}$/;
    if (!passwordRegex.test(form.password)) {
      return '8+ chars with 1 uppercase & 1 number';
    }

    if (!form.confirmPassword) {
      return 'Please confirm your password';
    }
    if (form.confirmPassword !== form.password) {
      return 'Passwords do not match';
    }

    if (!form.companyCode.trim()) {
      return 'Company code is required';
    }
    const companyCodeRegex = /^[A-Z0-9]{6}$/;
    if (!companyCodeRegex.test(form.companyCode.trim().toUpperCase())) {
      return 'Enter a valid 6-character company code';
    }

    if (!form.employeeId.trim()) {
      return 'Employee ID is required';
    }

    return null;
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage(null);
      setSuccessMessage(null);

      const resolvedCompanyCode = await validateCompanyCode(form.companyCode);
      const result = await signUp({
        email: form.email,
        password: form.password,
        fullName: form.fullName,
        companyCode: resolvedCompanyCode,
        employeeId: form.employeeId,
      });

      if (result.needsEmailConfirmation) {
        setSuccessMessage('Account created. Please verify your email, then log in.');
        navigate('/login');
        return;
      }

      setSuccessMessage('Account created successfully.');
      navigate('/');
    } catch (error) {
      setErrorMessage(mapError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const onGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      setSuccessMessage(null);
      await startGoogleOAuth({
        redirectTo: `${window.location.origin}/oauth/callback`,
        companyCode: form.companyCode,
        fullName: form.fullName,
        employeeId: form.employeeId,
      });
    } catch (error) {
      setErrorMessage(mapError(error));
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-appbg px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[80vh] w-full max-w-[1728px] items-center justify-center">
        <div className="w-full max-w-[560px] overflow-hidden rounded-[28px] border border-line bg-card shadow-floating">
          <div className="bg-primary-600 px-8 py-10 text-white">
            <p className="text-[14px] font-semibold uppercase tracking-[0.16em] text-primary-100">FlintPay</p>
            <h1 className="mt-3 text-[34px] font-extrabold tracking-[-0.02em]">Create Your Account</h1>
            <p className="mt-2 text-[14px] text-primary-100">Join FlintPay for secure corporate travel.</p>
          </div>

          <form className="space-y-5 p-8" onSubmit={onSubmit}>
            <div>
              <label
                className="mb-2 block text-[13px] font-bold uppercase tracking-[0.12em] text-primary-700"
                htmlFor="fullName"
              >
                Full Name
              </label>
              <input
                id="fullName"
                name="fullName"
                type="text"
                value={form.fullName}
                onChange={handleInput}
                placeholder="John Doe"
                autoComplete="name"
                className="h-12 w-full rounded-xl border border-line bg-primary-50 px-4 text-[15px] text-ink outline-none transition focus:border-primary-400 focus:bg-card"
              />
            </div>

            <div>
              <label
                className="mb-2 block text-[13px] font-bold uppercase tracking-[0.12em] text-primary-700"
                htmlFor="email"
              >
                Work Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleInput}
                placeholder="name@company.com"
                autoComplete="email"
                className="h-12 w-full rounded-xl border border-line bg-primary-50 px-4 text-[15px] text-ink outline-none transition focus:border-primary-400 focus:bg-card"
              />
            </div>

            <div>
              <label
                className="mb-2 block text-[13px] font-bold uppercase tracking-[0.12em] text-primary-700"
                htmlFor="password"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleInput}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  className="h-12 w-full rounded-xl border border-line bg-primary-50 px-4 pr-24 text-[15px] text-ink outline-none transition focus:border-primary-400 focus:bg-card"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] font-semibold uppercase tracking-[0.08em] text-primary-700"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div>
              <label
                className="mb-2 block text-[13px] font-bold uppercase tracking-[0.12em] text-primary-700"
                htmlFor="confirmPassword"
              >
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={handleInput}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  className="h-12 w-full rounded-xl border border-line bg-primary-50 px-4 pr-24 text-[15px] text-ink outline-none transition focus:border-primary-400 focus:bg-card"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((value) => !value)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] font-semibold uppercase tracking-[0.08em] text-primary-700"
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div>
              <label
                className="mb-2 block text-[13px] font-bold uppercase tracking-[0.12em] text-primary-700"
                htmlFor="companyCode"
              >
                Company Code
              </label>
              <input
                id="companyCode"
                name="companyCode"
                type="text"
                value={form.companyCode}
                onChange={handleInput}
                placeholder="e.g. A1B2C3"
                autoComplete="off"
                className="h-12 w-full rounded-xl border border-line bg-primary-50 px-4 uppercase text-[15px] tracking-[0.1em] text-ink outline-none transition focus:border-primary-400 focus:bg-card"
              />
            </div>

            <div>
              <label
                className="mb-2 block text-[13px] font-bold uppercase tracking-[0.12em] text-primary-700"
                htmlFor="employeeId"
              >
                Employee ID
              </label>
              <input
                id="employeeId"
                name="employeeId"
                type="text"
                value={form.employeeId}
                onChange={handleInput}
                placeholder="e.g. EMP-1024"
                autoComplete="off"
                className="h-12 w-full rounded-xl border border-line bg-primary-50 px-4 text-[15px] text-ink outline-none transition focus:border-primary-400 focus:bg-card"
              />
            </div>

            {errorMessage ? (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] font-medium text-red-600">{errorMessage}</p>
            ) : null}
            {successMessage ? (
              <p className="rounded-lg bg-emerald-50 px-3 py-2 text-[13px] font-medium text-emerald-700">{successMessage}</p>
            ) : null}

            <button
              type="submit"
              disabled={!canSubmit || isLoading}
              className="inline-flex h-12 w-full items-center justify-center rounded-full bg-primary-700 px-6 text-[15px] font-bold text-white transition hover:bg-primary-800 disabled:cursor-not-allowed disabled:bg-primary-300"
            >
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </button>

            <button
              type="button"
              onClick={onGoogleSignIn}
              disabled={isLoading}
              className="inline-flex h-12 w-full items-center justify-center rounded-full border border-line bg-card px-6 text-[15px] font-bold text-ink transition hover:bg-primary-50 disabled:cursor-not-allowed"
            >
              Continue with Google
            </button>

            <p className="pt-2 text-center text-[14px] text-muted">
              Already have an account?{' '}
              <Link to="/login" className="font-bold text-primary-700 hover:text-primary-800">
                Login
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
